import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Mail, Phone, Shield, CheckCircle2, AlertTriangle, Send, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

/**
 * CoOperatorCard — pre-provisioned Master Admin slot for Rebecca Parson.
 * Rendered inside the Vault Settings tab.
 *
 * Click GREEN button → backend fires two Postmark emails (or logs them in
 * stub mode) to both work + personal email. Stub mode is the default.
 */
const STATUS_PILL = {
  ready:       { label: 'Ready to onboard', bg: '#FEF3C7', fg: '#92400E' },
  provisioned: { label: 'User created', bg: '#DBEAFE', fg: '#1E40AF' },
  sent:        { label: 'Onboarding sent', bg: '#D1FAE5', fg: '#065F46' },
  activated:   { label: 'Activated', bg: '#A7F3D0', fg: '#064E3B' },
};

const CoOperatorCard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/co-operator`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState(await r.json());
    } catch (e) {
      setFlash({ err: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const confirmMsg = liveMode
      ? `LIVE MODE — this will send 2 real emails to rebecca@hibff.com AND rebecca@rparson.co.uk. Continue?`
      : `Stub mode — no emails will be sent (logged to backend only). Continue?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setFlash(null);
    try {
      const r = await fetch(`${API}/api/admin/co-operator/send-onboarding`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ live: liveMode }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setFlash({
        ok: liveMode
          ? 'Both emails dispatched via Postmark to work + personal address.'
          : `Stub mode — onboarding logged. TOTP enrolment URL: ${d.enrol_url_preview}`,
        enrol: d.enrol_url_preview,
      });
      load();
    } catch (e) {
      setFlash({ err: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-xl border p-5 animate-pulse" data-testid="co-operator-loading" style={{ borderColor: '#E0E0E0' }}><div className="h-4 w-32 bg-gray-200 rounded" /></div>;
  }
  if (!state) return null;

  const op = state.operator;
  const pill = STATUS_PILL[state.status] || STATUS_PILL.ready;

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="co-operator-card" style={{ borderColor: '#E0E0E0' }}>
      <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2"
        style={{ borderColor: '#F0F0F0', background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(245,131,31,0.04))' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#10B981', color: 'white' }}>
            <UserPlus size={18} />
          </div>
          <div>
            <h3 className="text-base font-black" style={{ color: '#1A1209' }}>Co-Operator</h3>
            <p className="text-[11px] text-gray-500">Pre-provisioned 2nd Master Admin · identical permissions to you</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: pill.bg, color: pill.fg }} data-testid="co-operator-status">
          <CheckCircle2 size={11} /> {pill.label}
        </span>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2"><UserPlus size={14} className="text-gray-400" /><span className="font-semibold text-gray-700">Name:</span><span className="text-gray-900" data-testid="co-operator-name">{op.name}</span></div>
          <div className="flex items-center gap-2"><Mail size={14} className="text-gray-400" /><span className="font-semibold text-gray-700">Work:</span><span className="text-gray-900 break-all">{op.email}</span></div>
          <div className="flex items-center gap-2"><Mail size={14} className="text-gray-400" /><span className="font-semibold text-gray-700">Personal:</span><span className="text-gray-900 break-all">{op.personal_email}</span></div>
          <div className="flex items-center gap-2"><Phone size={14} className="text-gray-400" /><span className="font-semibold text-gray-700">Mobile:</span><span className="text-gray-900">{op.phone_e164}</span></div>
          <div className="flex items-center gap-2 md:col-span-2"><Shield size={14} className="text-gray-400" /><span className="font-semibold text-gray-700">Role:</span><span className="text-orange-700 font-bold">Master Admin (identical to founder)</span></div>
        </div>

        {flash && (
          <div className={`px-3 py-2 rounded-lg text-xs ${flash.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="co-operator-flash">
            {flash.ok ? <CheckCircle2 size={12} className="inline mr-1" /> : <AlertTriangle size={12} className="inline mr-1" />}
            {flash.ok || flash.err}
            {flash.enrol && (
              <div className="mt-1.5 text-[11px] font-mono break-all opacity-70">{flash.enrol}</div>
            )}
          </div>
        )}

        <div className="border-t pt-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: '#F0F0F0' }}>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer" data-testid="co-operator-live-toggle">
            <input type="checkbox" checked={liveMode} onChange={(e) => setLiveMode(e.target.checked)} className="w-4 h-4 accent-orange-500" />
            <span className={liveMode ? 'text-red-700' : 'text-gray-500'}>
              {liveMode ? 'LIVE — emails will actually send' : 'Stub mode (no emails sent)'}
            </span>
          </label>
          <button
            onClick={send}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-black text-sm transition-all disabled:opacity-50 shadow-sm hover:shadow-md"
            style={{ backgroundColor: '#10B981', color: 'white' }}
            data-testid="co-operator-send-button"
          >
            {busy ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            {liveMode ? 'Send onboarding now (LIVE)' : 'Send onboarding now (stub)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoOperatorCard;
